import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
  useDetector,
  useEquipment,
  useUser,
} from "@services/servicesGenericsTables";
import { useLaboratory } from "@services/servicesGenericsTables/useLaboratory";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { useTypesUnits } from "@services/servicesGenericsTables/useTypesUnits";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";
import {
  MAutocomplete,
  MButton,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import ResultsTable from "../commonForms/resultsTable";
import dayjs from "dayjs";
import { useBackgroundCodes } from "@services/servicesGenericsTables/useBackgroundCodes";

const CalculatioSR = () => {
  const form = useFormContext();
  const { userList } = useUser();
  const { equipmentList } = useEquipment();
  const { laboratoriesList } = useLaboratory();
  const { currentCalculation, saveCalculate, loadingResults } = useAnalysis();
  const { typesUnitsis } = useTypesUnits();
  const { detectorsList } = useDetector();
  const filteredCalculation = useFilteredProcedures("CAL");
  const codeAnalysis = form.getValues("typeAnalysis")?.codigo;
  const { backgroundCodesList } = useBackgroundCodes();

  useEffect(() => {
    const resultToCalculation = form.getValues("result_to_calculation");
    const preparationPerformance = form.getValues("preparation_performance");
    
    // Priorizar result_to_calculation sobre preparation_performance
    const performanceValue = resultToCalculation || preparationPerformance;
    form.setValue("calculation_performance", performanceValue);

    const mg_torta = form.getValues("mg_torta");
    form.setValue("calculation_dry_wet_weight", mg_torta);
  }, [form.watch("result_to_calculation"), form.watch("preparation_performance"), form.watch("mg_torta")]);

  const handleCalculate = async () => {
    await saveCalculate(codeAnalysis as string);
  };

  const textButtonCalculation = "Calcular";

  return (
    <>
      {currentCalculation.index !== undefined && (
        <>
          <ResponsiveFormContainer columns={3}>
            <MAutocomplete
              key="calculation_analyst"
              name="calculation_analyst"
              options={userList}
              label="Seleccione un analista"
              getOptionLabel={(option) => option.username || ""}
              validation={{ required: "El analista es requerido" }}
            />
            <MDatePicker
              key="start_date_time"
              name="start_date_time"
              pickerType="datetime"
              label="Fecha y hora de inicio Toma de muestra"
              disabled
            />
            <MDatePicker
              key="end_date_time"
              name="end_date_time"
              pickerType="datetime"
              label="Fecha y hora fin Toma de muestra"
              disabled
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">
              {"Datos ficha del procesamiento, preparación y contaje:"}
            </Typography>
          </Grid>

          <ResponsiveFormContainer columns={3}>
            <MTextField
              key={JSON.stringify(form.getValues("calculation_amount_sample"))}
              label="Cantidad de muestra"
              name="calculation_amount_sample"
              type="text"
              disabled
              fullWidth
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_units"))}
              name="calculation_units"
              options={typesUnitsis}
              label="Unidades"
              getOptionLabel={(option) =>
                `${option.codigo} | ${option.descripcion}`
              }
              disabled
            />
            <MDatePicker
              key="calculation_start_date_precipitation"
              name="calculation_start_date_precipitation"
              pickerType="datetime"
              label="Fecha y hora precipitación tierras raras"
              disabled
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_tsie_white"))}
              name="calculation_tsie_white"
              label="Peso precipitado (mg)"
              type="text"
              disabled
              fullWidth
            />
            <MTextField
              key={JSON.stringify(form.getValues("calculation_performance"))}
              name="calculation_performance"
              label="Rendimiento"
              type="text"
              disabled
              fullWidth
            />

            <MTextField
              label="Peso Húmedo/ seco"
              name="calculation_dry_wet_weight"
              key={JSON.stringify(form.getValues("calculation_dry_wet_weight"))}
              type="text"
              disabled
              fullWidth
            />

            <MTextField
              key={JSON.stringify(form.getValues("calculation_shared"))}
              name="calculation_shared"
              label="Compartida"
              type="text"
              disabled
              fullWidth
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_equipment"))}
              name="calculation_equipment"
              options={equipmentList}
              label="Equipo de Contaje"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              disabled
              multiple={true}
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_detector"))}
              name="calculation_detector"
              options={detectorsList}
              label="Detector"
              getOptionLabel={(option) => `${option.name}` || ""}
              disabled
              multiple
            />
            <MDatePicker
              name="calculation_first_counting_date"
              pickerType="datetime"
              label="Fecha y hora inicio 1er contaje"
              disabled
            />
            <MTextField
              name="calculation_first_counting_time"
              placeholder="Tiempo total 1er contaje (min)"
              label="Tiempo total 1er contaje (min)"
              fullWidth
              disabled
            />
            <MTextField
              name="calculation_first_counting_cpm"
              placeholder="cpm 1er contaje"
              label="cpm 1er contaje"
              disabled
              fullWidth
            />
            <MDatePicker
              name="calculation_second_counting_date"
              pickerType="datetime"
              label="Fecha y hora inicio 2do contaje"
              disabled
            />
            <MTextField
              name="calculation_second_counting_time"
              placeholder="Tiempo total 2do contaje (min)"
              label="Tiempo total 2do contaje (min)"
              fullWidth
              disabled
            />
            <MTextField
              name="calculation_second_counting_cpm"
              placeholder="cpm 2do contaje"
              label="cpm 2do contaje"
              disabled
              fullWidth
            />
          </ResponsiveFormContainer>

          <Grid item xs={12} my={2}>
            <Typography variant="tagText">{"Datos a cumplimentar:"}</Typography>
          </Grid>

          <ResponsiveFormContainer columns={4}>
            <MAutocomplete
              key={JSON.stringify(form.getValues("laboratory"))}
              name="laboratory"
              options={laboratoriesList}
              label="Seleccione un Laboratorio"
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.descripcion}` || ""
              }
              validation={{ required: "El laboratorio es requerido" }}
            />

            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_procedure"))}
              name="calculation_procedure"
              options={filteredCalculation}
              label="Procedimiento MLPMA"
              getOptionLabel={(option) => `${option.numop}` || ""}
              disabled
            />
            <MTextField
              key={JSON.stringify(form.getValues("total_time_counting"))}
              name="total_time_counting"
              label="Tiempo contaje muestra (min)"
              fullWidth
              validation={{ required: "El tiempo de contaje es requerido" }}
            />
            <MAutocomplete
              key={JSON.stringify(form.getValues("calculation_fondo"))}
              name="calculation_fondo"
              options={backgroundCodesList}
              label="Código fondo beta"
              getOptionLabel={(option) =>
                `${option.proceso} - ${option.tipo_codigo}${
                  option.medio_filtro ? ` - ${option.medio_filtro}` : ""
                }` || ""
              }
              validation={{ required: "El fondo es requerido" }}
            />
            <MDatePicker
              name="calculation_date"
              pickerType="datetime"
              label="Fecha Cálculo"
              // minDate={dayjs(form.watch("calculation_counting_date"))}
            />
          </ResponsiveFormContainer>

          <ResponsiveFormContainer columns={4} mt={2}>
            <MTextField
              label="Tiempo de muestreo (min)"
              name="calculation_sampling_time_min"
              key="calculation_sampling_time_min"
              fullWidth
              disabled
            />
            <MTextField
              label="Diferencial de tiempo: F. precipitación. – F. final muestreo. (min)"
              name="calculation_precipitation_final_time"
              key="calculation_precipitation_final_time"
              fullWidth
              disabled
            />
            <MTextField
              label="t1: F. precipitación. - 1er contaje (min)"
              name="calculation_precipitation_first_counting"
              key="calculation_precipitation_first_counting"
              fullWidth
              disabled
            />
            <MTextField
              label="t2: F. precipitación. - 2do contaje (min)"
              name="calculation_precipitation_second_counting"
              key="calculation_precipitation_second_counting"
              fullWidth
              disabled
            />
          </ResponsiveFormContainer>

          {currentCalculation.id !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
              }}
            >
              <MButton
                onClick={handleCalculate}
                text={textButtonCalculation}
                color="secondary"
                loading={loadingResults}
                disabled={!!form.watch("idResultPreload")}
              />
            </Box>
          )}

          <ResultsTable />
        </>
      )}
    </>
  );
};

export default CalculatioSR;
